/**
 * Modifications.js
 * 
 * This module provides functions for modifying and extruding 3D geometries in the
 * Eagle Eye Designer application. It focuses on:
 * 
 * 1. Creating extruded 3D meshes from 2D shapes
 * 2. Modifying obstacle heights
 * 3. Adjusting obstacle offset boundaries
 * 
 * These functions handle the complex 3D geometry operations needed for
 * obstacles on roof segments, including proper elevation handling.
 */

import * as THREE from 'three'
import { POLYGONS } from "../Global/variables";
import { addOffset } from './Shapes';
import { COLORS } from '../Global/materials';

/**
 * Updates an obstacle's mesh/shape after its vertices (cords) have changed.
 * Rebuilds the extruded geometry + offset from newPoints.
 */
export function updateObstacleShape(segmentIndex, obstacleIndex, newPoints) {
    const obstruction = POLYGONS[segmentIndex]?.obstructions?.[obstacleIndex];
    if (!obstruction) return;

    const { type, height, offset } = obstruction;

    if (!obstruction.mesh) return;

    const oldMesh = obstruction.mesh;

    // Clean up old offset child (if any)
    const prevOffset = oldMesh.getObjectByName("offset");
    if (prevOffset) {
        prevOffset.removeFromParent();
        prevOffset.geometry.dispose();
        prevOffset.material.dispose();
    }

    // Save parent BEFORE removing the mesh from the scene
    const parent = oldMesh.parent;

    // Remove old mesh from scene graph
    if (parent) {
        parent.remove(oldMesh);
    }
    if (oldMesh.geometry) oldMesh.geometry.dispose();
    if (oldMesh.material) oldMesh.material.dispose();

    // If there's no parent, this obstacle is no longer in the scene
    // (e.g., it was deleted); bail out safely instead of crashing.
    if (!parent) {
        return;
    }

    // Build new 2D shape from updated 3D points (XY projection)
    const shapePoints = newPoints.map(p => new THREE.Vector2(p.x, p.y));
    const newShape = new THREE.Shape(shapePoints);

    // Re-extrude obstacle
    const color = obstruction.ogColor || COLORS.maroon;
    const extrudeMesh = addExtrude(newPoints, newShape, height, color, parent);

    // Recreate offset boundary with SAME offset & height
    const ret = addOffset(
        extrudeMesh,
        newPoints,
        type,
        newShape,
        offset,
        height,
        segmentIndex
    );

    // Update obstruction object
    obstruction.cords = ret.points;
    obstruction.polyShape = ret.shape;
    obstruction.mesh = ret.mesh;

    // Keep it visually selected (yellow) if you want
    obstruction.mesh.material.color.set(COLORS.yellow);
}

export function addExtrude(points, polyShape, height, color, scene) {
    // Extrusion settings - no bevel for clean edges
    let extrudeSettings = {
        bevelEnabled: false,
    };

    // Create the basic extrude geometry from the 2D shape
    let extrudeGeometry = new THREE.ExtrudeGeometry(polyShape, extrudeSettings);
    
    // Small tolerance for floating point comparisons
    const tolerance = 0.0001;
    
    // Adjust the Z coordinates of the geometry vertices to match the 3D points
    // and apply the height to the top face
    for(let c = 0; c < extrudeGeometry.attributes.position.array.length; c += 3) {
        let x = extrudeGeometry.attributes.position.array[c];
        let y = extrudeGeometry.attributes.position.array[c+1];
        let z = extrudeGeometry.attributes.position.array[c+2];
        
        // Check each input point to see if this vertex matches its X/Y coordinates
        for (let cord in points) {
            // If the X/Y coordinates match (within tolerance)
            if(Math.abs(x - points[cord].x) < tolerance && Math.abs(y - points[cord].y) < tolerance) {
                // For bottom face vertices (z=0 in the extrude geometry)
                if(z === 0) {
                    // Set Z to match the point's Z coordinate
                    extrudeGeometry.attributes.position.array[c+2] = points[cord].z;
                }
                // For top face vertices (z=1 in the extrude geometry)
                else if(z === 1) {
                    // Set Z to be the point's Z coordinate plus the height
                    extrudeGeometry.attributes.position.array[c+2] = points[cord].z + height;
                }
            }
        }
    }

    // Mark the geometry as needing an update
    extrudeGeometry.attributes.position.needsUpdate = true;
    
    // Create a wireframe representation of the extruded geometry
    let wireframeGeometry = new THREE.EdgesGeometry(extrudeGeometry);
    let wireframeMaterial = new THREE.LineBasicMaterial({ color: color });
    let wireframeMesh = new THREE.LineSegments(wireframeGeometry, wireframeMaterial);

    // Set visual properties for the wireframe
    wireframeMesh.material.transparent = true;
    wireframeMesh.material.opacity = 1;
    wireframeMesh.renderOrder = 1;  // Higher render order to ensure visibility
    wireframeMesh.material.depthTest = false;  // Always visible, even behind other objects

    // Add the wireframe to the scene (guarded by caller so scene should exist)
    scene.add(wireframeMesh);

    return wireframeMesh;
}

/**
 * Changes the height of an existing obstacle
 * 
 * This function updates the geometry of an obstacle to have a new height,
 * preserving its base position and shape.
 * 
 * @param {number} segmentIndex - Index of the segment containing the obstacle
 * @param {number} obstacleIndex - Index of the obstacle to modify
 * @param {number} height - New height for the obstacle in inches
 */
export function changeObstacleHeight(segmentIndex, obstacleIndex, height) {
    // Get the obstacle from the POLYGONS data structure
    let obstruction = POLYGONS[segmentIndex].obstructions[obstacleIndex];

    // Get the base coordinates of the obstacle
    let coordinates = obstruction.cords;
    
    // Extrusion settings - no bevel for clean edges
    let extrudeSettings = { bevelEnabled: false };
    
    // Create a new extrude geometry with the existing shape
    let extrudeGeo = new THREE.ExtrudeGeometry(obstruction.polyShape, extrudeSettings);

    // Small tolerance for floating point comparisons
    const tolerance = 0.0001;
    
    // Adjust the Z coordinates of the new geometry vertices
    // Similar process as in addExtrude, but updating an existing obstacle
    for(let c = 0; c < extrudeGeo.attributes.position.array.length; c += 3) {
        let x = extrudeGeo.attributes.position.array[c];
        let y = extrudeGeo.attributes.position.array[c+1];
        let z = extrudeGeo.attributes.position.array[c+2];
        
        for (let cord in coordinates) {
            if(Math.abs(x - coordinates[cord].x) < tolerance && Math.abs(y - coordinates[cord].y) < tolerance) {
                if(z == 0) {
                    // Bottom face - match the base Z coordinate
                    extrudeGeo.attributes.position.array[c+2] = coordinates[cord].z;
                }
                else if(z == 1) {
                    // Top face - base Z plus the new height
                    extrudeGeo.attributes.position.array[c+2] = coordinates[cord].z + height;
                }
            }
        }
    }
    
    // Create a wireframe from the new geometry
    let wireframeGeometry = new THREE.EdgesGeometry(extrudeGeo);

    // Clean up the old geometry
    obstruction.mesh.geometry.dispose();
    
    // Update the obstacle's mesh with the new geometry
    obstruction.mesh.geometry = wireframeGeometry;
    obstruction.mesh.material.transparent = true;
    obstruction.mesh.material.opacity = 1;
    obstruction.mesh.material.depthTest = false;
    obstruction.mesh.material.needsUpdate = true;
    
    // Update the height property in the obstacle data
    obstruction.height = height;
    
    // Mark the geometry as needing an update
    obstruction.mesh.geometry.attributes.position.needsUpdate = true;

    // ✅ NEW: Propagate height changes to all linked segments
    if (obstruction.isFlushed && obstruction.linkedSegments && obstruction.linkedSegments.length > 1) {

        obstruction.linkedSegments.forEach(idx => {
            if (idx === segmentIndex) return;
            const seg = POLYGONS[idx];
            const sharedObs = seg.obstructions.find(o => o === obstruction);
            if (sharedObs) sharedObs.height = height;
        });
    }
}


/**
 * Changes the offset boundary of an existing obstacle
 * 
 * This function updates the offset boundary visualization around an obstacle,
 * which represents the safety margin that panels should not overlap with.
 * 
 * @param {number} segmentIndex - Index of the segment containing the obstacle
 * @param {number} obstacleIndex - Index of the obstacle to modify
 * @param {number} offset - New offset distance in inches
 */
export function changeObstacleOffset(segmentIndex, obstacleIndex, offset) {
    // Get the obstacle from the POLYGONS data structure
    const obstruction = POLYGONS[segmentIndex].obstructions[obstacleIndex];
    
    // Find the existing offset boundary by name
    const prevOffset = obstruction.mesh.getObjectByName("offset");

    // Remove and clean up the previous offset boundary if it exists
    if (prevOffset) {
        prevOffset.removeFromParent();
        prevOffset.geometry.dispose();
        prevOffset.material.dispose();
    }
    
    // Create a new offset boundary with the new offset distance
    const {mesh, shape, points} = addOffset(
        obstruction.mesh,
        obstruction.cords,
        obstruction.type,
        obstruction.polyShape,
        offset,
        obstruction.height,
        segmentIndex
    );
    
    // Update the obstacle properties with the new values
    obstruction.offset = offset;
    obstruction.cords = points;
    obstruction.mesh = mesh;
    obstruction.polyShape = shape;
    
    // Set the color to yellow to indicate it's selected
    obstruction.mesh.material.color.set(COLORS.yellow);

    // ✅ NEW: Propagate offset updates to all linked segments
    if (obstruction.isFlushed && obstruction.linkedSegments && obstruction.linkedSegments.length > 1) {

        obstruction.linkedSegments.forEach(idx => {
            if (idx === segmentIndex) return;
            const seg = POLYGONS[idx];
            const sharedObs = seg.obstructions.find(o => o === obstruction);
            if (sharedObs) sharedObs.offset = offset;
        });
    }
}
